import { numberToMoneyMask, parseMoneyMask } from "@/lib/moneyMask";

export const FORNECEDOR_MIN_PARCELAS = 2;
export const FORNECEDOR_MAX_PARCELAS = 24;

export type ParcelaFormRow = {
  numero: number;
  valor: string;
  data_vencimento: string;
};

/** Divide o valor total em parcelas; centavos residuais vão para a última. */
export function splitValorEmParcelas(total: number, quantidade: number): number[] {
  if (quantidade < 1 || total <= 0) return [];
  const totalCents = Math.round(total * 100);
  const baseCents = Math.floor(totalCents / quantidade);
  const remainder = totalCents - baseCents * quantidade;
  return Array.from({ length: quantidade }, (_, index) => {
    const cents = baseCents + (index === quantidade - 1 ? remainder : 0);
    return cents / 100;
  });
}

/** Soma meses a uma data ISO; se o dia não existir no mês alvo, usa o último dia válido. */
export function addMonthsIso(isoDate: string, months: number): string {
  const match = isoDate.trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return isoDate;
  const year = Number(match[1]);
  const monthIndex = Number(match[2]) - 1;
  const day = Number(match[3]);
  const target = new Date(year, monthIndex + months, 1, 12, 0, 0, 0);
  const lastDay = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
  const targetDay = Math.min(day, lastDay);
  const result = new Date(target.getFullYear(), target.getMonth(), targetDay, 12, 0, 0, 0);
  const y = result.getFullYear();
  const m = String(result.getMonth() + 1).padStart(2, "0");
  const d = String(result.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export function criarParcelasFormEstrutura(
  valorTotal: number,
  quantidade: number,
  prevParcelas: ParcelaFormRow[] = [],
): ParcelaFormRow[] {
  const valores = valorTotal > 0 ? splitValorEmParcelas(valorTotal, quantidade) : [];
  return Array.from({ length: quantidade }, (_, index) => ({
    numero: index + 1,
    valor: valores[index] != null ? numberToMoneyMask(valores[index]) : "",
    data_vencimento: prevParcelas[index]?.data_vencimento ?? "",
  }));
}

/** @deprecated Use criarParcelasFormEstrutura sem data de vencimento automática. */
export function gerarParcelasForm(
  valorTotal: number,
  quantidade: number,
  primeiraDataVencimento: string,
): ParcelaFormRow[] {
  const valores = splitValorEmParcelas(valorTotal, quantidade);
  return valores.map((valor, index) => ({
    numero: index + 1,
    valor: numberToMoneyMask(valor),
    data_vencimento: addMonthsIso(primeiraDataVencimento, index),
  }));
}

export function somaParcelasValor(parcelas: ParcelaFormRow[]): number {
  return parcelas.reduce((sum, parcela) => sum + parseMoneyMask(parcela.valor), 0);
}

export function valoresParcelasIguaisTotal(total: number, parcelas: ParcelaFormRow[]): boolean {
  if (parcelas.length === 0) return false;
  const somaCents = Math.round(somaParcelasValor(parcelas) * 100);
  const totalCents = Math.round(total * 100);
  return somaCents === totalCents;
}

export function diffCentavosParcelas(total: number, parcelas: ParcelaFormRow[]): number {
  return Math.round(somaParcelasValor(parcelas) * 100) - Math.round(total * 100);
}

export function parseQuantidadeParcelas(raw: string): number | null {
  const digits = raw.replace(/\D/g, "");
  if (digits === "") return null;
  const value = parseInt(digits, 10);
  if (!Number.isFinite(value)) return null;
  return value;
}

export function quantidadeParcelasValida(value: number | null): value is number {
  return value != null && value >= FORNECEDOR_MIN_PARCELAS && value <= FORNECEDOR_MAX_PARCELAS;
}
